import type { AnonymousTicketDetail, AnonymousTicketSummary, TicketListResponse } from "@queueryte/contracts";
import { useEffect, useRef } from "react";
import { formatDuration } from "./analytics";

interface TicketDrawerProps {
  open:boolean;
  title:string;
  result:TicketListResponse|null;
  detail:AnonymousTicketDetail|null;
  loading:boolean;
  onClose:()=>void;
  onSelect:(ticket:AnonymousTicketSummary)=>void;
  onMore:()=>void;
  onBack:()=>void;
}

function ticketStatusLabel(value:string):string {
  return value==="serving"?"In service":value.charAt(0).toUpperCase()+value.slice(1);
}
function eventTypeLabel(value:string):string {
  return ({
    ticket_issued:"Ticket issued",ticket_called:"Service started",ticket_completed:"Ticket completed",
    ticket_recalled:"Recall correction",day_opened:"Business day opened",day_closed:"Business day closed",
    break_changed:"Break status changed",state_snapshot:"Queue state update",health_snapshot:"Kiosk health update",
    telemetry_gap:"Telemetry gap",
  } as Record<string,string>)[value]??value.replaceAll("_"," ");
}
function timeQualityLabel(value:string):string {
  return ({synced:"Clock time confirmed",estimated:"Clock time estimated",unknown:"Clock time unavailable"} as Record<string,string>)[value]??value;
}
function timestamp(value:string|null):string {
  return value===null?"Time unavailable":new Date(value).toLocaleString();
}

export function TicketDrawer({
  open,title,result,detail,loading,onClose,onSelect,onMore,onBack,
}:TicketDrawerProps) {
  const closeRef=useRef<HTMLButtonElement>(null);
  useEffect(()=>{if(open)closeRef.current?.focus();},[open]);
  useEffect(()=>{
    if(!open)return;
    const handler=(event:KeyboardEvent)=>{if(event.key==="Escape")onClose();};
    addEventListener("keydown",handler);
    return()=>removeEventListener("keydown",handler);
  },[open,onClose]);
  if(!open)return null;
  return <div className="ticket-drawer-layer" role="presentation" onMouseDown={(event)=>{if(event.target===event.currentTarget)onClose();}}>
    <aside className="ticket-drawer" role="dialog" aria-modal="true" aria-labelledby="ticket-drawer-title">
      <header><div>{detail&&<button type="button" className="drawer-back" onClick={onBack}>Back to list</button>}<p className="section-kicker">TICKET DETAILS</p>
        <h2 id="ticket-drawer-title">{detail?`Ticket ${detail.localTicketId.slice(0,8)}`:title}</h2></div>
        <button ref={closeRef} type="button" className="drawer-close" onClick={onClose} aria-label="Close ticket details">x</button></header>
      {loading&&<div className="drawer-loading" role="status">Loading ticket details...</div>}
      {!loading&&detail&&<div className="ticket-detail">
        <div className="ticket-status-line"><span className={`ticket-status ${detail.status}`}>{ticketStatusLabel(detail.status)}</span>
          {detail.incompleteTimestamp&&<span className="timestamp-warning">Some times unavailable</span>}</div>
        <dl>
          <div><dt>Issued</dt><dd>{timestamp(detail.issuedAt)}</dd></div>
          <div><dt>Service started</dt><dd>{timestamp(detail.calledAt)}</dd></div>
          <div><dt>Completed</dt><dd>{timestamp(detail.completedAt)}</dd></div>
          <div><dt>Wait time</dt><dd>{formatDuration(detail.waitSeconds)}</dd></div>
          <div><dt>Service time</dt><dd>{formatDuration(detail.serviceSeconds)}</dd></div>
          <div><dt>Wait target</dt><dd>{detail.slaMet===null?"Not available":detail.slaMet?"Within target":"Above target"}</dd></div>
        </dl>
        <h3>Event history</h3>
        <ol className="ticket-event-list">{detail.events.map((event)=><li key={event.eventId} className={event.corrected?"corrected":""}>
          <i/><div><strong>{eventTypeLabel(event.type)}</strong><span>{timestamp(event.occurredAt)} | {timeQualityLabel(event.timeQuality)}</span>
            {event.corrected&&<small>Excluded because a recall corrected this event</small>}</div></li>)}</ol>
      </div>}
      {!loading&&!detail&&<div className="ticket-list">
        {result?.tickets.length?<>{result.tickets.map((ticket)=><button type="button" key={ticket.localTicketId} onClick={()=>onSelect(ticket)}>
          <span className={`ticket-status ${ticket.status}`}>{ticketStatusLabel(ticket.status)}</span><div><strong>{ticket.localTicketId.slice(0,8)}</strong>
            <small>{ticket.siteName} | {ticket.queueName}</small></div><div className="ticket-list-times"><span>Wait {formatDuration(ticket.waitSeconds)}</span><span>Service {formatDuration(ticket.serviceSeconds)}</span></div>
          {ticket.incompleteTimestamp&&<i className="timestamp-dot" title="Some times unavailable"/>}</button>)}
          {result.nextCursor&&<button type="button" className="load-more" onClick={onMore}>Load more</button>}</>:
          <div className="analytics-empty compact-empty"><strong>No matching tickets</strong><span>Ticket details remain anonymous. Events without reliable clock time may not match this filter.</span></div>}
      </div>}
    </aside>
  </div>;
}
